import React from 'react';
import { Award, ExternalLink, Calendar, CheckCircle } from 'lucide-react';
import { certifications } from '../data/mock';

const Certifications = () => {
  return (
    <section id="certifications" className="py-20 bg-[rgb(17,17,19)]">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Professional <span className="text-[rgb(218,255,1)]">Certifications</span>
            </h2>
            <p className="text-xl text-[rgb(161,161,170)] max-w-3xl mx-auto leading-relaxed">
              Recognized expertise through industry-leading AI/ML certification programs
            </p>
          </div>

          {/* DeepLearning.AI Badge */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-3 bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl px-6 py-4">
              <div className="w-12 h-12 rounded-full bg-[rgb(218,255,1)]/10 flex items-center justify-center">
                <Award size={24} className="text-[rgb(218,255,1)]" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-white">DeepLearning.AI Specialization</div>
                <div className="text-sm text-[rgb(161,161,170)]">Andrew Ng • Stanford University</div>
              </div>
              <div className="text-[rgb(218,255,1)]">
                <CheckCircle size={20} />
              </div>
            </div>
          </div>

          {/* Certifications Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="group bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 hover:border-[rgb(218,255,1)] hover:transform hover:translate-y-[-4px] transition-all duration-300 relative overflow-hidden"
              >
                {/* Top accent border */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-[rgb(218,255,1)] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 rounded-2xl bg-[rgb(218,255,1)]/10 flex items-center justify-center text-[rgb(218,255,1)] group-hover:bg-[rgb(218,255,1)]/20 transition-colors duration-300 flex-shrink-0">
                    <Award size={28} />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[rgb(218,255,1)] transition-colors duration-300">
                      {cert.title}
                    </h3>
                    
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center text-[rgb(218,218,218)]">
                        <Award size={16} className="mr-2 text-[rgb(161,161,170)]" />
                        {cert.issuer}
                      </div>
                      
                      <div className="flex items-center text-[rgb(161,161,170)]">
                        <Calendar size={16} className="mr-2" />
                        Completed: {cert.date}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CheckCircle size={16} className="text-[rgb(218,255,1)]" />
                        <span className="text-sm text-[rgb(218,255,1)] font-medium">Verified</span>
                      </div>
                      
                      <button className="flex items-center space-x-2 text-[rgb(161,161,170)] hover:text-[rgb(218,255,1)] transition-colors duration-200">
                        <span className="text-sm">View Certificate</span>
                        <ExternalLink size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Skills Covered */}
          <div className="mt-16">
            <h3 className="text-2xl font-semibold text-white text-center mb-8">
              Skills <span className="text-[rgb(218,255,1)]">Covered</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-6">
                <div className="w-12 h-12 rounded-xl bg-[rgb(218,255,1)]/10 flex items-center justify-center mb-4">
                  <span className="text-xl">🧠</span>
                </div>
                <h4 className="text-lg font-semibold text-white mb-3">Neural Networks</h4>
                <ul className="space-y-2 text-sm text-[rgb(161,161,170)]">
                  <li>• Forward & Backward Propagation</li>
                  <li>• Deep Neural Networks</li>
                  <li>• Activation Functions</li>
                  <li>• Hyperparameter Tuning</li>
                </ul>
              </div>
              
              <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-6">
                <div className="w-12 h-12 rounded-xl bg-[rgb(218,255,1)]/10 flex items-center justify-center mb-4">
                  <span className="text-xl">🔍</span>
                </div>
                <h4 className="text-lg font-semibold text-white mb-3">Machine Learning</h4>
                <ul className="space-y-2 text-sm text-[rgb(161,161,170)]">
                  <li>• Supervised Learning</li>
                  <li>• Regularization Techniques</li>
                  <li>• Model Evaluation</li>
                  <li>• Feature Engineering</li>
                </ul>
              </div>
              
              <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-6">
                <div className="w-12 h-12 rounded-xl bg-[rgb(218,255,1)]/10 flex items-center justify-center mb-4">
                  <span className="text-xl">⚙️</span>
                </div>
                <h4 className="text-lg font-semibold text-white mb-3">ML Operations</h4>
                <ul className="space-y-2 text-sm text-[rgb(161,161,170)]">
                  <li>• Model Structuring</li>
                  <li>• Error Analysis</li>
                  <li>• Transfer Learning</li>
                  <li>• ML Project Lifecycle</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-16 text-center">
            <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-white mb-4">
                Continuous <span className="text-[rgb(218,255,1)]">Certification</span>
              </h3>
              <p className="text-[rgb(161,161,170)] mb-6 max-w-2xl mx-auto">
                I believe in staying current with the latest developments in AI/ML. Currently pursuing advanced 
                certifications in computer vision, NLP, and MLOps to further expand my expertise.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <span className="px-4 py-2 bg-[rgb(38,40,42)] rounded-lg text-sm text-[rgb(218,218,218)]">
                  Computer Vision (In Progress)
                </span>
                <span className="px-4 py-2 bg-[rgb(38,40,42)] rounded-lg text-sm text-[rgb(218,218,218)]">
                  MLOps (Planned)
                </span>
                <span className="px-4 py-2 bg-[rgb(38,40,42)] rounded-lg text-sm text-[rgb(218,218,218)]">
                  AWS ML (Planned)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Certifications;